import * as React from 'react';
import { Checkbox, Empty } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { TreeWrapper, TreeRoot, BranchContent, CheckBoxWrapper, ExpandIcon } from './styles';
import { SelectData } from './ResourceSelection';

interface Resource {
  default?: boolean;
  id?: string;
  name: string;
  resource: string;
  action?: React.ReactNode;
  custom?: boolean;
  children?: Resource[];
}

interface NormalizedResource extends Resource {
  selectedChildrenNumber?: number;
  selectionStatus?: 'all' | 'none' | 'some' | 'self';
  selected?: boolean;
  hasChildren?: boolean;
  shouldShow?: boolean;
  children?: NormalizedResource[];
  parents?: string[];
}

interface BranchState {
  expanded: string[];
}

interface BranchTreeProps {
  resources?: NormalizedResource[];
  onSelect?: (event: CheckboxChangeEvent, data: SelectData) => void;
  expanded?: false | string[];
  expandable?: boolean;
  initial?: boolean;
  searchTerm?: string;
  parentExpanded?: boolean;
}
class TreeBranchComponent extends React.PureComponent<BranchTreeProps, BranchState> {
  constructor(props: BranchTreeProps) {
    super(props);
    this.state = {
      expanded: this.props.expanded || [],
    };
  }

  toggleExpand = (resource: string) => () => {
    const { expanded } = this.state;

    if (expanded.includes(resource)) {
      this.setState({
        expanded: (expanded || []).filter((res: string) => res !== resource),
      });
    } else {
      this.setState({
        expanded: [...expanded, resource],
      });
    }
  };

  render() {
    const { resources, onSelect, expandable = true, initial, parentExpanded } = this.props;
    let data: (JSX.Element | null)[] | undefined | JSX.Element =
      resources &&
      resources
        .filter((resource) => resource.shouldShow)
        .map((resource: NormalizedResource, index: number) => {
          let subResources;
          const { shouldShow, id, children, selectionStatus, custom, action } = resource;
          if (!shouldShow) {
            return null;
          }
          const resourceId = id || resource.resource;

          if (children && children.length > 0) {
            subResources = children;
          }
          const expanded = this.state.expanded.includes(resourceId || '');

          let displayable = parentExpanded;
          if (typeof parentExpanded === 'undefined') {
            displayable = true;
          }
          const result = (
            <div key={`${index}.${resourceId}`} className="tree-branch__container">
              <BranchContent title={resource.resource}>
                <CheckBoxWrapper
                  displayable={displayable}
                  className="tree-branch__checkbox-wrapper"
                >
                  <Checkbox
                    onChange={(e) =>
                      onSelect &&
                      onSelect(e, {
                        checked: ['all', 'self'].includes(selectionStatus || ''),
                        resource,
                        value: resourceId,
                        name: resource.resource,
                        id: resourceId,
                      })
                    }
                    value={resourceId}
                    name={resource.resource}
                    id={resourceId}
                    indeterminate={selectionStatus === 'some'}
                    disabled={!!custom}
                    checked={custom ? true : ['all', 'self'].includes(selectionStatus || '')}
                  />
                </CheckBoxWrapper>
                <div style={{ marginLeft: 24 }}>
                  {expandable && children && (
                    <ExpandIcon
                      className="tree-branch__container_expand-icon"
                      expanded={expanded}
                      onClick={this.toggleExpand(resourceId as string)}
                    />
                  )}
                  <label
                    htmlFor={resourceId}
                    className="tree-branch__container_name"
                    style={{ cursor: 'pointer' }}
                  >
                    {resource.name || resource.resource}
                  </label>
                </div>
                {displayable && action && (
                  <BranchContent
                    style={{ marginTop: 0 }}
                    className="tree-branch__container_actions"
                  >
                    {action}
                  </BranchContent>
                )}
              </BranchContent>
              {subResources && (
                <TreeWrapper expanded={expanded} className="tree-branch__branch_container">
                  <TreeBranchComponent
                    parentExpanded={displayable && expanded}
                    expandable={expandable}
                    onSelect={onSelect}
                    resources={subResources}
                    expanded={this.state.expanded}
                  />
                </TreeWrapper>
              )}
            </div>
          );

          if (initial) {
            return <TreeRoot key={`${index}.${resourceId}`}> {result}</TreeRoot>;
          }
          return result;
        });

    if (!data || data.length === 0) {
      data = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return <div>{data}</div>;
  }
}

export default (props: BranchTreeProps) => (
  <div style={{ position: 'relative' }}>
    <TreeBranchComponent {...props} />
  </div>
);
